import { Machine } from "xstate";
import { toaster } from "@/utilities/toaster";
import { ProjectTagModule } from "@/store/modules/projectTag.module";
const projectTagsMachine = Machine({
    id: "projectTags",
    context: {
        tagOperationPerformed() { }
    },
    initial: "initialize",
    states: {
        initialize: {
            tags: "initialize",
            always: "gettingTeamsProjectsMembersAndTags",
        },
        gettingTeamsProjectsMembersAndTags: {
            tags: "gettingTeamsProjectsMembersAndTags",
            invoke: {
                id: "gettingTeamsProjectsMembersAndTags",
                src: async (context, event) => {
                    const data = await ProjectTagModule.GetTeamsProjectsMembersAndTags();
                    if (data.teams && data.teams.length > 0) {
                        return { appUserIsNotTeamLead: false };
                    }
                    else {
                        return { appUserIsNotTeamLead: true };
                    }
                },
                onDone: [
                    {
                        target: "appUserIsNotTeamLead",
                        cond: (context, event) => {
                            return event.data.appUserIsNotTeamLead;
                        },
                    },
                    { target: "gettingProjectTree" },
                ],
                onError: { target: "serviceUnavailable" },
            },
        },
        gettingProjectTree: {
            tags: "gettingProjectTree",
            initial: "gettingProjects",
            states: {
                gettingProjects: {
                    tags: "gettingProjects",
                    invoke: {
                        id: "gettingProjects",
                        src: async (context, event) => {
                            const noPayload = !event.payload;
                            const teams = ProjectTagModule.teams.map(X => X.id.toString());
                            const payload = noPayload ? { teams: teams, tags: [], members: [] } : event.payload;
                            const data = await ProjectTagModule.GetProjects({ fetchRequest: payload });
                            const projects = data?.projects;
                            if (!projects || projects.length === 0) {
                                if (noPayload) {
                                    return { noProjectAvailable: true };
                                }
                                else {
                                    return { noFilteredProjectAvailable: true };
                                }
                            }
                            else {
                                return { noProjectAvailable: false };
                            }
                        },
                        onDone: [
                            {
                                target: "#projectTags.noProjectAvailable",
                                cond: (context, event) => {
                                    return event.data.noProjectAvailable;
                                },
                            },
                            {
                                target: "noFilteredProjectAvailable",
                                cond: (context, event) => {
                                    return event.data.noFilteredProjectAvailable;
                                },
                            },
                            { target: "#projectTags.loadProjectTree" },
                        ],
                        onError: {
                            target: "#projectTags.serviceUnavailable"
                        },
                    },
                },
                noFilteredProjectAvailable: {
                    tags: "noFilteredProjectAvailable",
                    on: {
                        SELECT_PROJECT_TREE: { target: "#projectTags.selectProjectTree" },
                        GETTING_PROJECT_TREE: { target: "#projectTags.gettingProjectTree" },
                    },
                },
            }
        },
        selectProjectTree: {
            tags: "selectProjectTree",
            on: {
                NO_FILTERED_PROJECT_AVAILABLE: { target: "#projectTags.gettingProjectTree.noFilteredProjectAvailable" },
                GETTING_PROJECTS: { target: "#projectTags.gettingProjectTree.gettingProjects" },
                LOAD_PROJECT_TREE: { target: "loadProjectTree" },
                CLOSE_MODAL: { target: "idle" },
            },
        },
        loadProjectTree: {
            tags: "loadProjectTree",
            initial: "loadingProjectTree",
            states: {
                loadingProjectTree: {
                    tags: "loadingProjectTree",
                    on: {
                        PROJECT_TREE_LOADED: { target: "projectTreeLoaded" }
                    },
                },
                projectTreeLoaded: {
                    tags: "projectTreeLoaded",
                    on: {
                        GETTING_TEAMS_PROJECTS_MEMBERS_TAGS: { target: "#projectTags.gettingTeamsProjectsMembersAndTags" },
                        GETTING_PROJECT_TREE: { target: "#projectTags.gettingProjectTree" },
                        SELECT_PROJECT_TREE: { target: "#projectTags.selectProjectTree" },
                        REBUILD_PROJECT_TREE: { target: "#projectTags.rebuildProjectTree" },
                        LOAD_PROJECT_TREE: { target: "#projectTags.loadProjectTree" },
                        GETTING_PROJECT_INFO: { target: "#projectTags.gettingProjectInfo" },
                    }
                },
            }
        },
        gettingProjectInfo: {
            tags: "gettingProjectInfo",
            invoke: {
                id: "gettingProjectInfo",
                src: (context, event) => {
                    const projectId = ProjectTagModule.selectedProjectId;
                    if (projectId) {
                        return ProjectTagModule.GetProjectInfoById(projectId);
                    }
                },
                onDone: { target: "projectLoaded" },
                onError: { target: "serviceUnavailable" },
            },
        },
        idle: {
            tags: "idle",
            on: {
                GETTING_TEAMS_PROJECTS_MEMBERS_TAGS: { target: "gettingTeamsProjectsMembersAndTags" },
                GETTING_PROJECT_TREE: { target: "gettingProjectTree" },
                GETTING_PROJECT_INFO: { target: "gettingProjectInfo" },
                SELECT_PROJECT_TREE: { target: "selectProjectTree" },
                REBUILD_PROJECT_TREE: { target: "rebuildProjectTree" },
                LOAD_PROJECT_TREE: { target: "loadProjectTree" },
                PROJECT_LOADED: { target: "projectLoaded" },
                ASSIGN_TAG: { target: "assignTag" },
                UNASSIGN_TAG: { target: "unassignTag" },
            },
        },
        projectLoaded: {
            tags: "projectLoaded",
            on: {
                GETTING_TEAMS_PROJECTS_MEMBERS_TAGS: { target: "gettingTeamsProjectsMembersAndTags" },
                GETTING_PROJECT_TREE: { target: "gettingProjectTree" },
                GETTING_PROJECT_INFO: { target: "gettingProjectInfo" },
                SELECT_PROJECT_TREE: { target: "selectProjectTree" },
                REBUILD_PROJECT_TREE: { target: "rebuildProjectTree" },
                LOAD_PROJECT_TREE: { target: "loadProjectTree" },
                ASSIGN_TAG: { target: "assignTag" },
                UNASSIGN_TAG: { target: "unassignTag" },
            },
        },
        assignTag: {
            tags: "assignTag",
            on: {
                ASSIGNING_TAG: { target: "assigningTag" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        assigningTag: {
            tags: "assigningTag",
            invoke: {
                id: "assigningTag",
                src: (context, event) => {
                    const projectId = ProjectTagModule.selectedProjectId;
                    if (projectId) {
                        return ProjectTagModule.ManageTag({ projectId: projectId, operation: event.payload });
                    }
                },
                onDone: { target: "projectLoaded", actions: "tagOperationPerformed" },
                onError: { target: "assignTag" },
            },
        },
        unassignTag: {
            tags: "unassignTag",
            on: {
                UNASSIGNING_TAG: { target: "unassigningTag" },
                CLOSE_MODAL: { target: "projectLoaded" },
            },
        },
        unassigningTag: {
            tags: "unassigningTag",
            invoke: {
                id: "unassigningTag",
                src: (context, event) => {
                    const projectId = ProjectTagModule.selectedProjectId;
                    if (projectId) {
                        return ProjectTagModule.ManageTag({ projectId: projectId, operation: event.payload });
                    }
                },
                onDone: { target: "projectLoaded", actions: "tagOperationPerformed" },
                onError: { target: "unassignTag" },
            },
        },
        rebuildProjectTree: {
            tags: "rebuildProjectTree",
            on: {
                GETTING_PROJECT_TREE: { target: "gettingProjectTree" },
                LOAD_PROJECT_TREE: { target: "loadProjectTree" },
                PROJECT_LOADED: { target: "projectLoaded" },
            }
        },
        noProjectAvailable: {
            tags: "noProjectAvailable",
            type: "final"
        },
        appUserIsNotTeamLead: {
            tags: "appUserIsNotTeamLead",
            type: "final",
        },
        serviceUnavailable: {
            tags: "serviceUnavailable",
            type: "final",
        },
    },
}, {
    actions: {
        showErrorMessage: (context, event) => {
            toaster.ShowSuccess(event.data.errorMessages[0]);
        },
        showSuccessMessage: (context, event) => {
            toaster.ShowSuccess(event.data.successMessages[0]);
        },
        tagOperationPerformed: (context, event) => {
            if (context.tagOperationPerformed) {
                context.tagOperationPerformed();
            }
        },
    },
    guards: {},
    activities: {},
    services: {},
});
export default projectTagsMachine;
